import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { CheckCircleIcon, MessageCircleIcon, XIcon, ZoomInIcon } from 'lucide-react';
interface WarehouseFeature {
  name: string;
}
interface Warehouse {
  id: number;
  name: string;
  area: string;
  image: string;
  features: WarehouseFeature[];
  whatsappLink: string;
}
const warehouses: Warehouse[] = [{
  id: 1,
  name: 'Bodega 1',
  area: '1,226 Metros Cuadrados',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3dF17C6akHiUEb1aD4xeA9/BODEGA_1.jpg',
  features: [{
    name: 'Área para Carga y Descarga'
  }, {
    name: 'Oficina'
  }, {
    name: 'Servicios Sanitarios'
  }, {
    name: 'Seguridad y Vigilancia'
  }, {
    name: 'Muro Perimetral'
  }],
  whatsappLink: 'https://wa.me/50245607548?text=Hola%2C%20estoy%20interesado%20en%20la%20Bodega%201%20de%201%2C226%20m%C2%B2.'
}, {
  id: 2,
  name: 'Bodega 2',
  area: '1,494 Metros Cuadrados',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4BPtw1ZiBMEr7DNMTg1WjU/BODEGA_2.jpg',
  features: [{
    name: 'Entrada Principal'
  }, {
    name: 'Oficina'
  }, {
    name: 'Servicios Sanitarios'
  }, {
    name: 'Seguridad y Vigilancia'
  }, {
    name: 'Muro Perimetral'
  }],
  whatsappLink: 'https://wa.me/50245607548?text=Hola%2C%20estoy%20interesado%20en%20la%20Bodega%202%20de%201%2C494%20m%C2%B2.'
}];
export function WarehouseRentals() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  return <>
      <section id="bodegas" className="bg-gradient-to-b from-black via-zinc-900 to-black py-20 px-4 relative overflow-hidden scroll-mt-20" data-id="element-206">
        {/* Background elements */}
        <div className="absolute inset-0 opacity-5" data-id="element-207">
          <div className="absolute inset-0" style={{
          backgroundImage: `
              linear-gradient(rgba(252, 211, 77, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(252, 211, 77, 0.3) 1px, transparent 1px)
            `,
          backgroundSize: '80px 80px'
        }} data-id="element-208" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-209">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-16" data-id="element-210">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-211">
              Bodegas en <span className="text-yellow-400" data-id="element-212">Renta</span>
            </h2>
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: 96
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-6" data-id="element-213" />
            <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-214">
              Espacios industriales estratégicamente ubicados, listos para
              operar
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-215">
            {warehouses.map((warehouse, index) => <motion.div key={warehouse.id} initial={{
            opacity: 0,
            y: 50
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.2
          }} className="group relative bg-gradient-to-br from-zinc-900 to-black border border-zinc-800 rounded-xl overflow-hidden" data-id="element-216">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/0 via-yellow-400/0 to-yellow-400/0 group-hover:from-yellow-400/5 group-hover:via-yellow-400/10 group-hover:to-yellow-400/5 transition-all duration-500" data-id="element-217" />

                {/* Image */}
                <div className="relative h-64 overflow-hidden cursor-pointer" onClick={() => setSelectedImage(warehouse.image)} data-id="element-218">
                  <img src={warehouse.image} alt={`${warehouse.name} - ${warehouse.area}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-219" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" data-id="element-220" />

                  {/* Zoom indicator */}
                  <motion.div initial={{
                opacity: 0
              }} whileHover={{
                opacity: 1
              }} className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-id="element-221">
                    <div className="bg-yellow-400 text-black px-4 py-2 rounded-lg font-semibold flex items-center gap-2 shadow-[0_0_20px_rgba(252,211,77,0.5)]" data-id="element-222">
                      <ZoomInIcon size={20} data-id="element-223" />
                      Ver Plano Completo
                    </div>
                  </motion.div>

                  {/* Warehouse name badge */}
                  <div className="absolute top-4 left-4 z-10" data-id="element-224">
                    <div className="bg-yellow-400 text-black px-4 py-2 rounded-lg font-bold shadow-[0_0_20px_rgba(252,211,77,0.4)]" data-id="element-225">
                      {warehouse.name}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="relative p-8" data-id="element-226">
                  <div className="mb-6" data-id="element-227">
                    <h3 className="text-3xl font-bold text-yellow-400 mb-2" data-id="element-228">
                      {warehouse.area}
                    </h3>
                    <div className="h-px bg-gradient-to-r from-yellow-400 via-yellow-400/50 to-transparent" data-id="element-229" />
                  </div>

                  <div className="space-y-3 mb-8" data-id="element-230">
                    {warehouse.features.map((feature, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: 0.1 * idx
                }} className="flex items-center gap-3 text-gray-300" data-id="element-231">
                        <CheckCircleIcon size={20} className="text-yellow-400 flex-shrink-0" data-id="element-232" />
                        <span data-id="element-233">{feature.name}</span>
                      </motion.div>)}
                  </div>

                  <Button variant="primary" href={warehouse.whatsappLink} className="w-full" data-id="element-234">
                    <MessageCircleIcon size={20} data-id="element-235" />
                    Consultar Disponibilidad
                  </Button>
                </div>

                {/* Corner accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-400/5 blur-3xl group-hover:bg-yellow-400/10 transition-all duration-500" data-id="element-236" />
              </motion.div>)}
          </div>

          {/* Additional info */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="mt-12 text-center" data-id="element-237">
            <p className="text-gray-400 text-lg" data-id="element-238">
              Ambas bodegas cuentan con acceso directo desde la Ruta al
              Atlántico y seguridad 24/7
            </p>
          </motion.div>
        </div>
      </section>

      {/* Image Modal/Lightbox with scroll */}
      <AnimatePresence data-id="element-239">
        {selectedImage && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm overflow-y-auto" onClick={() => setSelectedImage(null)} data-id="element-240">
            <div className="min-h-screen flex items-start justify-center p-4 py-20" data-id="element-241">
              {/* Close button - fixed position */}
              <motion.button initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} transition={{
            delay: 0.1
          }} onClick={() => setSelectedImage(null)} className="fixed top-4 right-4 z-10 bg-yellow-400 text-black p-3 rounded-full hover:bg-yellow-300 transition-colors shadow-[0_0_30px_rgba(252,211,77,0.5)]" whileHover={{
            scale: 1.1,
            rotate: 90
          }} whileTap={{
            scale: 0.9
          }} data-id="element-242">
                <XIcon size={24} data-id="element-243" />
              </motion.button>

              {/* Image container - scrollable */}
              <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} exit={{
            scale: 0.9,
            opacity: 0
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 25
          }} className="relative w-full max-w-6xl" onClick={e => e.stopPropagation()} data-id="element-244">
                <div className="relative bg-zinc-900 rounded-lg p-2 shadow-[0_0_50px_rgba(252,211,77,0.3)]" data-id="element-245">
                  <img src={selectedImage} alt="Plano de bodega" className="w-full h-auto rounded-lg" data-id="element-246" />

                  {/* Decorative corners */}
                  <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-yellow-400 rounded-tl-lg pointer-events-none" data-id="element-247" />
                  <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-yellow-400 rounded-tr-lg pointer-events-none" data-id="element-248" />
                  <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-yellow-400 rounded-bl-lg pointer-events-none" data-id="element-249" />
                  <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-yellow-400 rounded-br-lg pointer-events-none" data-id="element-250" />
                </div>

                {/* Instructions - below image */}
                <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 20
            }} transition={{
              delay: 0.2
            }} className="text-center text-gray-400 text-sm mt-6" data-id="element-251">
                  Desplázate para ver el plano completo • Click fuera de la
                  imagen para cerrar
                </motion.p>
              </motion.div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}